import * as React from 'react';
import {useMemo} from 'react';

import useRecommendations from '../../items/item-spinner/use-recommendations';
import {CategoryResult} from './CategoryResult';
import {FilterChips} from './FilterChips';
import {GetRecoItemsParams} from '../../monetate/monetate.types';
import {ItemSpinner} from '../../items/item-spinner/ItemSpinner';
import {MonetateService} from '../../../client/monetate/monetate.service';
import {pushGaEvent} from '../../../client/ga/ga.functions';
import {SearchResult, SubCat} from '../search.types';
import {URLBuilder} from '../../../server/tools/url-builder/url-builder.class';
import {User} from '../../users/user.class';
import {useService} from '../../react/ServiceContext';
import {PageHeader} from '../../ui/PageHeader/PageHeader';

interface CategoryResultsProps {
    buildURL: URLBuilder;
    debugWebcat: boolean;
    resultList?: SearchResult;
    stepIdPath: string;
    user: User;
}

export const CategoryResults = ({buildURL, debugWebcat, resultList, stepIdPath, user}: CategoryResultsProps) => {
    const componentName = `CategoryResults`;
    const monetateService: MonetateService = useService(`monetateService`);

    // Get recommendations
    const getRecoItemsParams: GetRecoItemsParams = useMemo(
        () => ({
            scheme: `category1_rr`,
            webcat: resultList.webcatMeta?.name,
            stepIdPath,
        }),
        [resultList, stepIdPath],
    );

    /*
    Rejoin split webcats if one of the lists is empty
     */
    const shouldRejoinSplitWebcats =
        resultList?.splitWebcats?.popular?.length === 0 ||
        resultList?.splitWebcats?.other?.length === 0 ||
        (user.hasCustomCatalogs() && user.hasCurrentPcode);
    const recommendations = useRecommendations(getRecoItemsParams, monetateService);

    const filterOutCrossMerch = (subCat: SubCat) => {
        if (user.hasCustomCatalogs() && user.hasCurrentPcode) {
            return subCat.numItems;
        }
        return true;
    };

    /**
     * Template
     */
    return (
        <div>
            {resultList.description && <div className="tw-mb-3">{resultList.description}</div>}
            <FilterChips
                buildURL={buildURL}
                pushGaEvent={(gaEventOptions) =>
                    pushGaEvent(`ga4_navigation_facet`, {
                        ...gaEventOptions,
                        ...{
                            click_action: `remove_facet`,
                            click_category_1: `facet_chip`,
                            click_category_2: null,
                            click_path: location.pathname,
                            click_type: `remove_facet`,
                        },
                    })
                }
                resultList={resultList}
            ></FilterChips>
            {resultList.splitWebcats ? (
                shouldRejoinSplitWebcats ? (
                    <div className="tw-flex tw-mb-3 tw-flex-wrap">
                        {resultList.splitWebcats.popular.filter(filterOutCrossMerch).map((subCat, index) => (
                            <CategoryResult
                                buildURL={buildURL}
                                debugWebcat={debugWebcat}
                                key={index}
                                resultList={resultList}
                                subCat={subCat}
                                user={user}
                            />
                        ))}
                        {resultList.splitWebcats.other.filter(filterOutCrossMerch).map((subCat, index) => (
                            <CategoryResult
                                buildURL={buildURL}
                                debugWebcat={debugWebcat}
                                key={index}
                                resultList={resultList}
                                subCat={subCat}
                                user={user}
                            />
                        ))}
                    </div>
                ) : (
                    <>
                        <div className="tw-text-[1.75rem] tw-mb-2 tw-font-normal">Popular Categories</div>
                        <div className="tw-flex tw-flex-wrap">
                            {resultList.splitWebcats.popular.filter(filterOutCrossMerch).map((subCat, index) => (
                                <CategoryResult
                                    buildURL={buildURL}
                                    debugWebcat={debugWebcat}
                                    key={index}
                                    resultList={resultList}
                                    subCat={subCat}
                                    user={user}
                                />
                            ))}
                        </div>
                        {resultList.splitWebcats.other?.filter(filterOutCrossMerch).length > 0 && (
                            <>
                                <div className="tw-text-[1.75rem] tw-mb-2 tw-font-normal tw-my-3 tw-pt-1">Additional Categories</div>
                                <div className="tw-mb-3 tw-flex tw-flex-wrap">
                                    {resultList.splitWebcats.other.filter(filterOutCrossMerch).map((subCat, index) => (
                                        <CategoryResult
                                            buildURL={buildURL}
                                            debugWebcat={debugWebcat}
                                            key={index}
                                            resultList={resultList}
                                            subCat={subCat}
                                            user={user}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </>
                )
            ) : (
                <div className="tw-flex tw-mb-3 tw-flex-wrap">
                    {resultList.subCatsByCount?.filter(filterOutCrossMerch).map((subCat, index) => (
                        <CategoryResult
                            buildURL={buildURL}
                            debugWebcat={debugWebcat}
                            key={index}
                            resultList={resultList}
                            subCat={subCat}
                            user={user}
                        />
                    ))}
                </div>
            )}
            {resultList.layout === `cat` && recommendations?.items?.length > 0 && (
                <ItemSpinner
                    componentName={componentName}
                    gaItemListName="scheme_category1_rr"
                    label={recommendations.schemeExplanation}
                    loaded={recommendations.loaded}
                    maxSlides={7}
                    orderableItems={recommendations.items}
                    user={user}
                />
            )}
            {resultList.relatedCategories?.length > 0 && (
                <>
                    <PageHeader
                        title="Related Categories"
                        type="h4"
                    />
                    <div className="tw-overflow-x-auto tw-pt-2 tw-px-0 tw-pb-3 tw-flex">
                        {resultList.relatedCategories.map((categorySuggestion, index) => (
                            <a
                                className="tw-my-0 tw-mx-2 tw-py-[13px] tw-px-3 tw-border tw-border-gray-700 tw-text-gray-700 tw-rounded-[50px] tw-whitespace-nowrap"
                                href={`/browse/${categorySuggestion.categoryPath}`}
                                key={index}
                            >
                                {categorySuggestion.name}
                            </a>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};
